// /components/CreateInvitationModal/EmailListManager.tsx
import React from 'react';
import { useTranslation } from 'react-i18next';
import { Row, Col, Button } from 'react-bootstrap';
import { AiOutlinePlus } from 'react-icons/ai';
import * as yup from 'yup';
import TextField from 'components/Form/FormTextField';
import { FormApi } from 'final-form';

type ValidationResult = {
  valid: string[];
  invalid: string[];
  duplicated: string[];
};

type EmailListManagerProps = {
  form: FormApi<any>;
  label: string;
  field: string; // e.g., 'email'
  listField: string; // e.g., 'emails'
  errorField: string; // e.g., 'emailError'
  noItemsMessage: string;
};

// Helper para no repetir la lógica de validación
const handleAddEmails = async (
  form: FormApi<any>,
  {
    t, field, listField, errorField,
  }: { t: any, field: string, listField: string, errorField: string },
) => {
  const formState = form.getState();
  const emailString = formState.values[field];
  if ( !emailString ) return;

  const emailSchema = yup.string().email( t( 'admin.invitations.errors.invalidEmail' ) );
  const currentEmails = formState.values[listField] || [];
  const potentialEmails = emailString.split( ';' ).map( ( e: string ) => e.trim() ).filter( Boolean );

  const results = await potentialEmails.reduce( async (
    accPromise: Promise<ValidationResult>,
    email: string,
  ) => {
    const accumulator = await accPromise;
    if ( currentEmails.includes( email ) || accumulator.valid.includes( email ) ) {
      accumulator.duplicated.push( email );
    } else {
      try {
        await emailSchema.validate( email );
        accumulator.valid.push( email );
      } catch ( err ) {
        accumulator.invalid.push( email );
      }
    }
    return accumulator;
  }, Promise.resolve( { valid: [], invalid: [], duplicated: [] } ) );

  if ( results.valid.length > 0 ) {
    form.change( listField, [...currentEmails, ...results.valid] );
  }

  const errorMessages = [];
  if ( results.invalid.length > 0 ) {
    errorMessages.push( `Emails inválidos: ${results.invalid.join( ', ' )}` );
  }
  if ( results.duplicated.length > 0 ) {
    errorMessages.push( `Emails duplicados: ${results.duplicated.join( ', ' )}` );
  }

  form.change( errorField, errorMessages.length > 0 ? errorMessages.join( '. ' ) : undefined );
  form.change( field, undefined );
  form.resetFieldState( field );
};

function EmailListManager( {
  form, label, field, listField, errorField, noItemsMessage,
}: EmailListManagerProps ) {
  const { t } = useTranslation();
  const values = form.getState()?.values || {};
  const emailList: string[] = values[listField] || [];

  const onAddClick = () => handleAddEmails( form, {
    t, field, listField, errorField,
  } );
  const onKeyDown = ( e: React.KeyboardEvent ) => {
    if ( e.key === 'Enter' ) {
      e.preventDefault();
      onAddClick();
    }
  };

  const onRemoveClick = ( indexToRemove: number ) => {
    const newList = emailList.filter( ( _, index ) => index !== indexToRemove );
    form.change( listField, newList );
  };

  return (
    <>
      <Row className="gx-1">
        <Col>
          <TextField
            field={field}
            label={label}
            showErrors={false}
            classNames={{ formGroup: 'mb-0' }}
            onKeyDown={onKeyDown}
          />
          {values[errorField] && (
            <div className="invalid-feedback d-block">{values[errorField]}</div>
          )}
        </Col>
        <Col xs="auto">
          <Button size="sm" variant="primary" onClick={onAddClick}>
            <AiOutlinePlus />
          </Button>
        </Col>
      </Row>

      <div className="email-list mt-2 p-2 border rounded" style={{ minHeight: '80px', maxHeight: '150px', overflowY: 'auto' }}>
        {emailList.length > 0 ? (
          emailList.map( ( email: string, index: number ) => (
            <Row key={`${email}`} className="align-items-center mb-1">
              <Col>{email}</Col>
              <Col xs="auto">
                <Button size="sm" variant="link" onClick={() => onRemoveClick( index )}>
                  <i className="icon-cancel" />
                </Button>
              </Col>
            </Row>
          ) )
        ) : (
          <div className="text-muted small p-2">{noItemsMessage}</div>
        )}
      </div>
    </>
  );
}

export default EmailListManager;
